/**
 * Экран режима «Предложения».
 *
 * Предложение показывается целиком — со знаками препинания и заглавными
 * буквами. Разбивать его на слова нельзя: именно переходы между словами и
 * пунктуация отличают реальный текст от списка слов (ТЗ §6, режим 5).
 *
 * Над текстом стоит только то, ради чего подобрано это предложение; сколько
 * их осталось — под текстом (ТЗ FR-EN-84).
 */

import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Card, EmptyState } from '@type2x/ui';
import { remainingSentences, type SentencesSession } from '@type2x/feature-sentence-practice';

import type { SessionId } from '@type2x/shared-contracts';

import type { Runtime } from '../runtime.js';
import { currentSentenceTask } from '../state/sentences.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface SentencesScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly session: SentencesSession;
  /** Идентификатор всей сессии: предложения — её части, а не отдельные сессии. */
  readonly sessionId: SessionId;
  readonly onSentenceDone: (run: TypingSession) => void;
}

export function SentencesScreen({
  runtime,
  t,
  session,
  sessionId,
  onSentenceDone,
}: SentencesScreenProps): React.JSX.Element {
  const task = currentSentenceTask(session);
  if (task === null) {
    return (
      <Card title={t('sentences.title')}>
        <EmptyState title={t('sentences.emptyTitle')} body={t('sentences.empty')} />
      </Card>
    );
  }

  const left = remainingSentences(session).length;
  const done = session.drills.length - left;

  return (
    <DrillLayout
      t={t}
      title={t('sentences.title')}
      now={
        <span className="t2x-muted">
          {task.word === null ? t('sentences.transfer') : t('sentences.focus', { word: task.word })}
        </span>
      }
      progress={{
        value: session.drills.length === 0 ? 0 : done / session.drills.length,
        label: t('sentences.progress'),
      }}
      details={
        <>
          <p className="t2x-muted">{t('sentences.remaining', { count: left })}</p>
          <p className="t2x-muted">{t('sentences.hint')}</p>
        </>
      }
    >
      <TypingScreen
        // Ключ включает номер предложения: следующее предложение — новый
        // прогон, а не продолжение прежнего.
        key={`${sessionId}:${session.currentIndex}`}
        runtime={runtime}
        t={t}
        title={t('sentences.typing')}
        hint={t('sentences.hint')}
        sessionId={sessionId}
        layoutId={session.layoutId}
        targetText={task.text}
        hintLevel={task.hintLevel}
        compact
        onFinished={onSentenceDone}
      />
    </DrillLayout>
  );
}
